import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

import { useBreakpoint } from '@/hooks/useBreakpoint';

const SLATE_EDITOR_SELECTOR = '[data-slate-editor="true"]';
const SETTLE_MS = 150;

export function useShouldHideProposalFooter(
  contentRef: RefObject<HTMLElement | null>
): boolean {
  const isLg = useBreakpoint('lg');
  const hasVisualViewport =
    typeof window !== 'undefined' &&
    typeof window.visualViewport !== 'undefined';
  const enabled = !isLg && hasVisualViewport;

  const [bodyFocused, setBodyFocused] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setBodyFocused(false);
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;

    const isBody = (el: Element | null) =>
      !!(
        el &&
        contentRef.current?.contains(el) &&
        el.closest(SLATE_EDITOR_SELECTOR)
      );

    // Initialize from the already-focused element so a viewport/breakpoint
    // change into mobile while the editor is focused hides the footer
    // immediately, instead of waiting for the next focusin.
    setBodyFocused(isBody(document.activeElement));

    const onFocusIn = (e: FocusEvent) => {
      if (isBody(e.target as Element | null)) {
        if (timer) {
          clearTimeout(timer);
        }
        timer = null;
        setBodyFocused(true);
      }
    };

    const onFocusOut = () => {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        timer = null;
        setBodyFocused(isBody(document.activeElement));
      }, SETTLE_MS);
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);

    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [enabled, contentRef]);

  return enabled && bodyFocused;
}
